import React, { useState, useEffect } from "react";
import NextImage from "next/image";
import Container from "react-bootstrap/Container";
import DynamicLink from "./DynamicLink";
import Title from "./Title";
import Text from "./Text";

export default function CarouselCards({ 
	items, 
	hasImages = true, 
	backgroundColor = "#f8f9fa",
	cardImageHeight = 250 
}) {
	const [currentIndex, setCurrentIndex] = useState(0);
	const [itemsPerView, setItemsPerView] = useState(4);

	// Handle responsive behavior
	useEffect(() => {
		const handleResize = () => {
			if (typeof window !== 'undefined') {
				if (window.innerWidth <= 768) {
					setItemsPerView(1);
				} else if (window.innerWidth <= 992) {
					setItemsPerView(2);
				} else if (window.innerWidth <= 1200) {
					setItemsPerView(3);
				} else {
					setItemsPerView(4);
				}
			}
		};
		
		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const maxIndex = Math.max(0, items.length - itemsPerView);

	const nextSlide = () => {
		setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
	};

	const prevSlide = () => {
		setCurrentIndex(prev => Math.max(prev - 1, 0));
	};

	const goToSlide = (index) => {
		setCurrentIndex(Math.min(index, maxIndex));
	};

	return (
		<div className="carousel-container">
			<div className="carousel-nav-container">
				<button 
					className="carousel-nav prev-btn" 
					onClick={prevSlide}
					disabled={currentIndex === 0}
					aria-label="Previous items"
				>
					&#8249;
				</button>
				<button 
					className="carousel-nav next-btn" 
					onClick={nextSlide}
					disabled={currentIndex >= maxIndex}
					aria-label="Next items"
				>
					&#8250;
				</button>
			</div>
			
			<div className="carousel-track-container">
				<div 
					className="carousel-track"
					style={{ 
						transform: `translateX(-${currentIndex * (100 / items.length)}%)`,
						width: `${(items.length * 100) / itemsPerView}%`
					}}
				>
					{items.map((item, index) => (
						<div 
							key={item.id || index} 
							className="carousel-slide"
							style={{ width: `${100 / items.length}%` }}
						>
							<div className={`card ${hasImages ? 'has-image' : 'no-image'}`}>
								{/* Card Image - only if hasImages is true */}
								{hasImages && item.image && (
									<div className="card-image-wrapper">
										<NextImage
											src={item.image}
											alt={item.title}
											width={400}
											height={cardImageHeight}
											className="card-image"
											style={{ 
												width: "100%", 
												height: "100%", 
												objectFit: "cover" 
											}}
										/>
									</div>
								)}
								
								{/* Card Content */}
								<div className="card-content">
									{/* Badge/Tag */}
									{item.badge && (
										<div className="badge-container">
											<span className={`badge ${item.badgeType || 'default'}`}>
												{item.badge}
											</span>
										</div>
									)}

									<Title element="h5" text={item.title} marginBottom={1} fontFamily="Athelas" />
									<Text
										text={item.description}
										textAlign="start"
										size="13"
										weight="100"
										textJustify="start"
										className="mb-0 text-muted"
									/>
					
									
									<DynamicLink 
										href={item.link} 
										className={`action-btn ${item.buttonType || 'learn-more'}`} 
										underlineStyle={true}
									>
										{item.buttonText || 'LEARN MORE'}
									</DynamicLink>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			<style jsx="true">{`
				/* Navigation Container - Top Right */
				.carousel-nav-container {
					position: absolute;
					top: -70px;
					right: 15px;
					display: flex;
					gap: 8px;
					z-index: 999;
				}

				/* Navigation Buttons */
				.carousel-nav {
					position: relative;
					background: white;
					border: 1px solid #d1d5db;
					border-radius: 6px;
					width: 40px;
					height: 40px;
					display: flex;
					align-items: center;
					justify-content: center;
					font-size: 18px;
					font-weight: 600;
					color: #6b7280;
					cursor: pointer;
					box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
				}

				.carousel-nav:hover:not(:disabled) {
					background: #f9fafb;
					border-color: #9ca3af;
					color: #374151;
				}

				.carousel-nav:disabled {
					opacity: 0.4;
					cursor: not-allowed;
					background: #f9fafb;
				}

				/* Carousel Container */
				.carousel-container {
					position: relative;
				}

				/* Carousel Track */
				.carousel-track-container {
					overflow: hidden;
					border-radius: 8px;
				}

				.carousel-track {
					display: flex;
					transition: transform 0.4s ease;
				}

				.carousel-slide {
					flex-shrink: 0;
					padding: 0 15px;
				}

				/* Card Styles */
				.card {
					background: white;
					border-radius: 12px;
					box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
					height: 100%;
					display: flex;
					flex-direction: column;
					overflow: hidden;
					transition: all 0.3s ease;
				}

				.card.has-image {
					min-height: 500px;
				}

				.card.no-image {
					min-height: 320px;
				}

				.card-image-wrapper {
					height: ${cardImageHeight}px;
					position: relative;
					overflow: hidden;
				}

				.card-content {
					padding: 1rem;
					flex: 1;
					display: flex;
					flex-direction: column;
					justify-content: space-between;
				}

				.badge-container {
					margin-bottom: 0.5rem;
				}

				.badge {
					padding: 0.2rem 0.8rem;
					border-radius: 25px;
					font-size: 0.65rem;
					font-weight: 700;
					color: #000;
					text-transform: uppercase;
					letter-spacing: 1px;
					display: inline-block;
				}

				.badge.default {
					background: #D4A574;
					color: #000;
				}

				.badge.green {
					background: #2dd4bf;
					color: #000;
				}

				.badge.yellow {
					background: #8b5cf6;
					color: #000;
				}

				.card-title {
					font-size: 1.5rem;
					font-weight: 700;
					line-height: 1.3;
					margin-bottom: 1rem;
					color: #2c5aa0;
				}

				.card-description {
					color: #666;
					line-height: 1.6;
					margin-bottom: 0.5rem;
					flex: 1;
					font-size: 1rem;
				}

				.action-btn {
					color: #2c3e50;
					text-decoration: none;
					font-weight: 700;
					font-size: 0.9rem;
					text-transform: uppercase;
					letter-spacing: 1px;
					margin-top: auto;
					padding: 0.2rem 0.5rem;
					background: none;
					border: none;
					outline: none;
					cursor: pointer;
					position: relative;
					display: inline;
					width: fit-content;
					align-self: flex-start;
				}

				.action-btn::after {
					content: "";
					position: absolute;
					bottom: 0;
					left: 0;
					width: 100%;
					height: 2px;
					background-color: #D4A574;
				}

				.action-btn:hover {
					background-color: #34495e;
					text-decoration: none;
					transform: translateY(-1px);
					box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
					color: white;
				}

				/* Responsive Design */
				@media (max-width: 992px) {
					.carousel-nav {
						width: 38px;
						height: 38px;
						font-size: 16px;
					}

					.card-image-wrapper {
						height: 200px;
					}

					.card-title {
						font-size: 1.3rem;
					}
				}

				@media (max-width: 768px) {
					.carousel-nav {
						width: 36px;
						height: 36px;
						font-size: 15px;
					}

					.carousel-nav-container {
						gap: 6px;
					}

					.card-image-wrapper {
						height: 180px;
					}

					.card-content {
						padding: 1.5rem;
					}

					.card-title {
						font-size: 1.2rem;
					}

					.card-description {
						font-size: 0.9rem;
					}

					.card.has-image {
						min-height: 400px;
					}

					.card.no-image {
						min-height: 280px;
					}
				}

				@media (max-width: 576px) {
					.carousel-nav {
						width: 34px;
						height: 34px;
						font-size: 14px;
					}

					.carousel-nav-container {
						gap: 4px;
					}
				}
			`}</style>
		</div>
	);
}